import {
    Box,
    HStack,
    SimpleGrid,
    useRadio,
    useRadioGroup,
} from '@chakra-ui/react';
import { MdMoveToInbox, MdOutbox } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { Heading, Icon, Text } from 'components/v4';
import { TRANSFER_IN, TRANSFER_OUT } from '../../utils/transferDirection';

const OPTIONS = [
    {
        value: TRANSFER_IN,
        label: 'Transfer IN',
        icon: MdMoveToInbox,
        iconColor: 'blue.500',
        description:
            'Numbers you are adding to the company. These are already on the Teams tenant and are moving to you as the new Operator',
    },
    {
        value: TRANSFER_OUT,
        label: 'Transfer OUT',
        icon: MdOutbox,
        iconColor: 'red.500',
        description:
            'Numbers previously provided by you. These numbers have now moved, or are moving to, another Operator.',
    },
];

const DirectionOption = ({ option, darkMode, ...radioProps }) => {
    const { getInputProps, getCheckboxProps, state } = useRadio(radioProps);
    const isChecked = state.isChecked;

    return (
        <Box as="label" height="100%">
            <input {...getInputProps()} />
            <Box
                {...getCheckboxProps()}
                data-walkthroughid={`/confirm-transfer/direction/${option.value}`}
                cursor="pointer"
                borderRadius="md"
                padding={4}
                height="100%"
                bg={
                    isChecked
                        ? darkMode
                            ? 'whiteAlpha.100'
                            : 'blue.50'
                        : darkMode
                          ? 'dark.tcap'
                          : 'gray.50'
                }
                border={isChecked ? '2px solid' : '1px solid'}
                borderColor={
                    isChecked
                        ? 'brand.500'
                        : darkMode
                          ? 'dark.tcap-borderGray'
                          : 'gray.200'
                }
                _hover={{ borderColor: 'brand.500' }}
                _focusVisible={{ boxShadow: 'outline' }}>
                <HStack justify="space-between" mb={3}>
                    <HStack spacing={3}>
                        <Icon
                            as={option.icon}
                            color={option.iconColor}
                            sx={{ width: '24px', height: '24px' }}
                        />
                        <Heading as="h4" size="sm" marginBottom={0}>
                            {option.label}
                        </Heading>
                    </HStack>
                    <Box
                        aria-hidden
                        boxSize="20px"
                        borderRadius="full"
                        border="2px solid"
                        borderColor={isChecked ? 'brand.500' : 'gray.400'}
                        display="flex"
                        alignItems="center"
                        justifyContent="center">
                        {isChecked && (
                            <Box
                                boxSize="10px"
                                borderRadius="full"
                                bg="brand.500"
                            />
                        )}
                    </Box>
                </HStack>
                <Text fontSize="sm" marginBottom={0}>
                    {option.description}
                </Text>
            </Box>
        </Box>
    );
};

/**
 * The IN / OUT choice shown in the Confirm Transfer modal before the number
 * picker (3065).
 */
const TransferDirectionOptions = ({ value, onChange }) => {
    const { darkMode } = useSelector((state) => state.settings);
    const { getRootProps, getRadioProps } = useRadioGroup({
        name: 'transferDirection',
        value: value ?? '',
        onChange,
    });

    return (
        <>
            <Text mb={4}>
                Are you transferring numbers <b>IN</b> to your Operator or{' '}
                <b>OUT</b> to another Operator?
            </Text>
            <SimpleGrid
                {...getRootProps()}
                columns={{ base: 1, md: 2 }}
                spacing={4}>
                {OPTIONS.map((option) => (
                    <DirectionOption
                        key={option.value}
                        option={option}
                        darkMode={darkMode}
                        {...getRadioProps({ value: option.value })}
                    />
                ))}
            </SimpleGrid>
        </>
    );
};

export default TransferDirectionOptions;
